(ns io.daskul.tutorial.webapp.views.lead-detail.vm
  "Pure view-model logic for the Lead's Trial Bookings roster (SMS-148,
   SMS-178).

   Carries NO UI dependency (no `io.daskul.webapp.ui` / `@tabler` import) so it
   runs under this webapp's :node-test target (`bb tutorial:webapp:test`) —
   the JSX render lives in `io.daskul.tutorial.webapp.leads`. Mirrors the
   SMS-35 linked-leads split: pure row-building / chip-mapping logic is
   node-tested, the visual layout is e2e-verified
   (frontend-component-architecture — separate logic from layout).

   Responsibilities:
   - `sa->row`     — shape one SessionAttendee (+ included :session) into a
                     flat row view-model the Trial Bookings roster renders.
   - chip variants — map an SA state / a lead source to a Mantine color +
                     human label per 0013 §Design tokens."
  (:require [clojure.string :as str]))

(defn- blank->nil [s]
  (when (and s (not (str/blank? s))) s))

;; ============================================================================
;; Status-chip variant mapping (0013 §Design tokens)
;; ============================================================================
;;
;; Spec colors for the SessionAttendee status chip:
;;   Scheduled = violet · Attended = blue · No-show = red.
;; Cancelled / rescheduled are history states (gray). The map is keyword- and
;; string-keyed so it survives the JSON round-trip either way.

(def sa-state->chip
  "SessionAttendee state → {:label :color} for the roster status chip.
   Colors are Mantine theme keys (brand = violet primary)."
  {"scheduled"   {:label "Scheduled" :color "brand"}
   "attended"    {:label "Attended"  :color "blue"}
   "no-show"     {:label "No-show"   :color "red"}
   "cancelled"   {:label "Cancelled" :color "gray"}
   "rescheduled" {:label "Rescheduled" :color "gray"}})

(defn sa-chip
  "Resolve the status chip for an SA state. Falls back to a gray humanized
   chip for unknown states so a row is never chip-less."
  [state]
  (or (get sa-state->chip state)
      {:label (->> (str/split (str (or state "")) #"-")
                   (map str/capitalize)
                   (str/join " "))
       :color "gray"}))

(def lead-source->chip
  "Lead source → {:label :color} for the Source chip. Facebook reads as the
   Messenger brand blue; everything else is a neutral gray pill."
  {"facebook"          {:label "Facebook"          :color "blue"}
   "google-search"     {:label "Google Search"     :color "gray"}
   "referral-parent"   {:label "Parent Referral"   :color "gray"}
   "referral-external" {:label "External Referral"  :color "gray"}
   "walk-in"           {:label "Walk-in"           :color "gray"}
   "event"             {:label "Event"             :color "gray"}
   "flyer"             {:label "Flyer"             :color "gray"}
   "tarpaulin"         {:label "Tarpaulin"         :color "gray"}
   "other"             {:label "Other"             :color "gray"}})

(defn source-chip
  "Resolve the Source chip for a lead's :lead-source, or nil when absent."
  [lead-source]
  (when-let [src (blank->nil lead-source)]
    (or (get lead-source->chip src)
        {:label (->> (str/split src #"-") (map str/capitalize) (str/join " "))
         :color "gray"})))

;; ============================================================================
;; SessionAttendee → roster row view-model
;; ============================================================================

(def ^:private month-abbrev
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- format-session-when
  "Render a session's wall clock as 'Jun 20, 1:00 PM' from its naive :date
   ('YYYY-MM-DD') + :start-time ('HH:MM:SS'), or nil. No tz — the value is
   already the school's local wall clock (#590), so this agrees with every
   other trial surface by construction rather than by sharing a tz."
  [date start-time]
  (when (and (blank->nil date) (blank->nil start-time))
    (let [[_y m d] (mapv #(js/parseInt % 10) (str/split date #"-"))
          [h mm] (mapv #(js/parseInt % 10) (str/split start-time #":"))
          ampm (if (>= h 12) "PM" "AM")
          h12 (let [x (mod h 12)] (if (zero? x) 12 x))]
      (str (nth month-abbrev (dec m)) " " d ", "
           h12 ":" (when (< mm 10) "0") mm " " ampm))))

(defn- teacher-name
  "Teacher full name from the included :session, or nil."
  [session]
  (or (blank->nil (get-in session [:teacher :full-name]))
      (blank->nil (get-in session [:teacher :person :full-name]))
      (blank->nil (:teacher-name session))))

(defn sa->row
  "Shape one SessionAttendee (with included :session) + its parent Lead into a
   flat roster row view-model. The child name + age + program come from the
   LEAD (one Lead = one child); the session date/time + teacher come from the
   SA's included Session.

   Returns:
     {:sa-id :state :chip {:label :color}
      :child-name :age :program-name
      :when (\"Jun 20, 1:00 PM\" | nil) :teacher (name | nil)
      :transitions (available FSM transitions from :_meta.fsm | nil)
      :made-up? (boolean)}"
  ;; `tz` is still accepted in opts for call-site compatibility but no longer
  ;; used — #590 session times are already the school's wall clock.
  [{:keys [lead]} sa]
  (let [session (:session sa)
        child   (or (blank->nil (str/trim (str (:student-first-name lead) " "
                                               (:student-last-name lead))))
                    (blank->nil (:attendee-name sa))
                    "Trial child")
        age     (:age lead)]
    {:sa-id        (:id sa)
     :state        (:state sa)
     :chip         (sa-chip (:state sa))
     :child-name   child
     :age          age
     :program-name (or (blank->nil (:name (:program lead)))
                       (blank->nil (:program-name lead)))
     :when         (format-session-when (:date session) (:start-time session))
     :teacher      (teacher-name session)
     ;; SMS-162: the SA's available FSM transitions (framework-enriched under
     ;; :_meta), so the roster can drive quick actions via `transition-action`
     ;; (which reads each transition's :params for its derived modal).
     :transitions  (get-in sa [:_meta :fsm :transitions])
     ;; SMS-171: this row has been superseded by a makeup — the bidirectional
     ;; :data.makeup pointer set on the OLD row is {:new-session-attendee-id
     ;; <id>} (the NEW makeup row instead carries :old-session-attendee-id,
     ;; pointing backward — that shape means "I AM the makeup", not "I've
     ;; been made up", so it's deliberately excluded here). Unlike reschedule
     ;; (which changes :state to "rescheduled", already chip-visible), a
     ;; makeup leaves the old row's :state untouched (still "no-show" /
     ;; "cancelled"), so this flag is the only signal a superseded row has.
     :made-up?     (some? (get-in sa [:makeup :new-session-attendee-id]))}))

(defn sa-rows
  "Build roster rows for a lead's SessionAttendees. Active (non-rescheduled)
   rows first, history (rescheduled/cancelled) last; within each group, most
   recently created first — a stable sort keeps that newest-first order
   while still grouping active-before-history. Newest-first surfaces the
   lead's current booking without scrolling past no-show/makeup history.

   Exactly one row is flagged :is-active? true — the most recently created
   non-:rescheduled SA (the server's :default-sort is created-at ascending,
   so it's the LAST non-rescheduled entry in `sas`, computed before the
   display order is reversed). Only that row's undo control renders
   (SMS-165/171): undoing an older, superseded SA — e.g. one a makeup or
   reschedule already replaced — would resurrect a booking that's no longer
   current, leaving two simultaneously-active bookings.

   `opts` is {:lead :tz} (passed through to `sa->row`)."
  [opts sas]
  (let [rows (mapv #(sa->row opts %) sas)
        active-id (->> rows (remove #(= "rescheduled" (:state %))) last :sa-id)
        flagged (mapv #(assoc % :is-active? (= active-id (:sa-id %))) rows)]
    (->> flagged
         reverse
         (sort-by #(if (#{"rescheduled" "cancelled"} (:state %)) 1 0))
         vec)))
